import React, { useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleContext";
import { track } from "../lib/analytics";
import "./KidoChat.css";

/* KidoChat
   ----------------------------------------------------------------------
   Floating chat widget. Site-wide. Powered by Claude via a server route
   (wired in Task 3). Voice input via Web Speech API (Task 2). Safety +
   rate-limit + disclaimer (Task 4).

   LANGUAGES (see I18N.md)
   Every visible string is a locale triple. The visitor's language is
   sent to the API as `locale` so Kido replies in it, and every failure
   message is OUR translated fallback keyed off the server's error
   code: the server's English `message` string is never shown. */

const STARTERS = [
  { en: "Tell me about the programs", ar: "حدثني عن البرامج",                 ru: "Расскажи о программах" },
  { en: "What is the UK trip",        ar: "ما هي رحلة بريطانيا",              ru: "Что такое поездка в Великобританию" },
  { en: "How do I start",             ar: "كيف أبدأ",                          ru: "Как начать" },
];

const GREETING = {
  en: "Hi, I am Kido. I can explain our programs and trips and help you find the next step. What would you like to know.",
  ar: "مرحباً، أنا Kido. أستطيع أن أشرح لك برامجنا ورحلاتنا وأساعدك في تحديد الخطوة التالية. ما الذي تريد معرفته؟",
  ru: "Здравствуйте, я Kido. Я расскажу о наших программах и поездках и помогу найти следующий шаг. Что вы хотите узнать?",
};

/* Fallback messages by error kind. All point the parent to a human
   channel so even when the brain is offline the page still moves them
   forward. Latin phone numbers inside the Arabic copy carry the
   Unicode isolate pair (U+2066 / U+2069), the string equivalent of
   <bdi>, so "+971 52 836 0206" does not visually reorder in RTL. */
const FALLBACKS = {
  rate_limited: {
    en: "I have answered a lot in this session. To keep things fair, please WhatsApp our team on +971 52 836 0206 or book a free discovery call from the home page. We respond within 24 hours.",
    ar: "لقد أجبت عن أسئلة كثيرة في هذه الجلسة. للحفاظ على العدل، راسل فريقنا على واتساب على ⁦+971 52 836 0206⁩ أو احجز مكالمة تعريفية مجانية من الصفحة الرئيسية. نرد خلال 24 ساعة.",
    ru: "Я ответил на многое в этой сессии. Чтобы всё было по-честному, напишите нашей команде в WhatsApp на +971 52 836 0206 или запишитесь на бесплатную вводную консультацию на главной странице. Мы отвечаем в течение 24 часов.",
  },
  network: {
    en: "I cannot reach the server right now. Please check your connection or WhatsApp our team on +971 52 836 0206 and we will reply within 24 hours.",
    ar: "لا أستطيع الوصول إلى الخدمة الآن. تحقق من اتصالك أو راسل فريقنا على واتساب على ⁦+971 52 836 0206⁩ وسنرد خلال 24 ساعة.",
    ru: "Сейчас я не могу связаться с сервером. Проверьте соединение или напишите нашей команде в WhatsApp на +971 52 836 0206, и мы ответим в течение 24 часов.",
  },
  /* timeout, upstream_error, server_misconfigured, http_5xx, etc. */
  unknown: {
    en: "Sorry, I am having trouble right now. You can WhatsApp our team on +971 52 836 0206 or book a free discovery call from the home page and we will reply within 24 hours.",
    ar: "عذراً، أواجه مشكلة الآن. يمكنك مراسلة فريقنا على واتساب على ⁦+971 52 836 0206⁩ أو حجز مكالمة تعريفية مجانية من الصفحة الرئيسية وسنرد خلال 24 ساعة.",
    ru: "Извините, у меня сейчас сбой. Вы можете написать нашей команде в WhatsApp на +971 52 836 0206 или записаться на бесплатную вводную консультацию на главной странице, и мы ответим в течение 24 часов.",
  },
};

/* Speech recognition language per site language. */
const SPEECH_LANGS = { en: "en-US", ar: "ar-AE", ru: "ru-RU" };

const I_CHAT = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const I_CLOSE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6"  x2="6"  y2="18" />
    <line x1="6"  y1="6"  x2="18" y2="18" />
  </svg>
);

const I_SEND = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="22" y1="2"  x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const I_MIC = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8"  y1="23" x2="16" y2="23" />
  </svg>
);

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "ssr";
  try {
    const existing = window.sessionStorage.getItem("kido.sessionId");
    if (existing) return existing;
    const id = (window.crypto?.randomUUID?.() || String(Math.random()).slice(2));
    window.sessionStorage.setItem("kido.sessionId", id);
    return id;
  } catch {
    return "anon";
  }
}

/* Web Speech API surface differs between browsers (webkit prefix in
   Safari and older Chrome). Returns the constructor or null when the
   browser doesn't support speech recognition at all. */
function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/* Server route. The Anthropic API key NEVER leaves the function side
   of /api/kido (Firebase Cloud Function, see functions/index.js).
   On any failure the caller surfaces the friendly fallback. */
const KIDO_ENDPOINT = "/api/kido";

class KidoBrainError extends Error {
  constructor(kind, status) {
    super(kind);
    this.kind = kind;
    this.status = status || 0;
  }
}

/* Request contract: POST { sessionId, messages, locale }.
   `locale` is "en" | "ar" | "ru" and tells Kido which language to
   answer in. The server validates it and falls back to "en". */
async function callBrain(messages, sessionId, locale) {
  let res;
  try {
    res = await fetch(KIDO_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sessionId, messages, locale }),
    });
  } catch {
    throw new KidoBrainError("network");
  }
  if (!res.ok) {
    let body = {};
    try { body = await res.json(); } catch {}
    /* Only the error CODE is read. The server's `message` is English,
       so the client renders its own translated fallback instead. */
    throw new KidoBrainError(body.error || `http_${res.status}`, res.status);
  }
  const data = await res.json().catch(() => ({}));
  if (!data?.reply) throw new KidoBrainError("empty_reply");
  return String(data.reply).trim();
}

export default function KidoChat() {
  const { t, lp, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: t(GREETING) },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  /* Voice input state. `voiceSupported` decides whether we render the
     mic at all (no-op render where the browser doesn't support the
     Web Speech API, e.g. Firefox without flags, older Safari).
     `listening` toggles the visible "listening" indicator and animates
     the mic button while SpeechRecognition is active. */
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const scrollRef = useRef(null);
  const inputRef  = useRef(null);
  const recogRef  = useRef(null);
  const transcriptRef = useRef("");
  /* When the parent presses the mic to stop, set this so the onend
     handler doesn't auto-restart the recognizer. Used by send() too:
     hitting send stops voice cleanly. */
  const stoppedByUserRef = useRef(false);

  /* Detect Web Speech API support on mount. We never construct a
     recognition instance here; we only flip the flag so the mic
     button renders. The instance is created lazily on first press
     so that the "site-loaded but never used voice" path costs zero. */
  useEffect(() => {
    setVoiceSupported(!!getSpeechRecognitionCtor());
  }, []);

  /* Language switched before the first question: greet again in the
     new language. An in-progress conversation is left alone, so the
     parent never sees their own history rewritten. */
  useEffect(() => {
    setMessages((m) => (
      m.length === 1 && m[0].role === "assistant"
        ? [{ role: "assistant", content: t(GREETING) }]
        : m
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  /* Clean up an active recognition session on unmount. */
  useEffect(() => () => {
    if (recogRef.current) {
      try { recogRef.current.abort(); } catch {}
      recogRef.current = null;
    }
  }, []);

  /* Keep the message list pinned to the latest as it grows. */
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending, open]);

  /* Auto-focus the input when the panel opens. */
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(timer);
    }
  }, [open]);

  /* Lock body scroll on small screens while the full-height sheet is up. */
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 560) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  /* Our own translated failure text, chosen by the server's error
     code. The server never supplies parent-facing copy. */
  const fallbackFor = (kind) => t(FALLBACKS[kind] || FALLBACKS.unknown);

  const send = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || sending) return;
    /* Pressing send while voice is still listening: stop it cleanly
       so the recognizer doesn't keep capturing background audio
       while Kido replies. */
    if (listening && recogRef.current) {
      stoppedByUserRef.current = true;
      try { recogRef.current.stop(); } catch {}
    }
    const next = [...messages, { role: "user", content: clean }];
    setMessages(next);
    track("kido_message", { chars: String(text || "").length });
    setInput("");
    setSending(true);
    try {
      const reply = await callBrain(next, sessionId, locale);
      setMessages(m => [...m, { role: "assistant", content: reply }]);
    } catch (e) {
      const kind = e?.kind || "unknown";
      setMessages(m => [...m, { role: "assistant", content: fallbackFor(kind) }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  /* Toggle voice input.
     Owner spec 2026-06-13: the recognizer must NOT auto-shut on
     silence. It keeps listening until the parent presses the mic
     again, or presses send. Pauses are fine, they keep speaking,
     the recognizer keeps going.
     Implementation: continuous = true, plus an onend auto-restart
     guarded by stoppedByUserRef so we only respect the stop the
     parent actually asked for. */
  const toggleVoice = () => {
    if (!voiceSupported) return;
    if (listening) {
      stoppedByUserRef.current = true;
      try { recogRef.current?.stop(); } catch {}
      return;
    }
    setVoiceError("");
    stoppedByUserRef.current = false;
    transcriptRef.current = input;
    startRecognition();
  };

  /* Build, wire, and start a SpeechRecognition instance. Pulled out
     so the onend auto-restart path can reuse it without rebuilding
     all the handlers inline. */
  const startRecognition = () => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setVoiceSupported(false);
      return;
    }
    const recog = new Ctor();
    /* Listen in the language the visitor is browsing in. */
    recog.lang = SPEECH_LANGS[locale] || SPEECH_LANGS.en;
    /* continuous = true means the recognizer stays alive across
       silences. Even with this, some browsers (Chrome notably)
       still end the stream after ~60 s of silence; the onend
       auto-restart below makes that invisible to the parent. */
    recog.continuous = true;
    recog.interimResults = true;
    recog.maxAlternatives = 1;

    recog.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += chunk;
        else interim += chunk;
      }
      /* When a result is marked final, fold it into the persistent
         transcript so a later interim doesn't overwrite it. */
      if (final) {
        const base = transcriptRef.current ? transcriptRef.current.trim() + " " : "";
        transcriptRef.current = (base + final).replace(/\s+/g, " ").trimStart();
        setInput(transcriptRef.current);
      } else if (interim) {
        const base = transcriptRef.current ? transcriptRef.current.trim() + " " : "";
        setInput((base + interim).replace(/\s+/g, " ").trimStart());
      }
    };

    recog.onerror = (e) => {
      /* Map browser errors to a short, parent-friendly message. */
      const code = e?.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setVoiceError(t({
          en: "Microphone permission was blocked. You can type instead.",
          ar: "تم حجب إذن الميكروفون. يمكنك الكتابة بدلاً من ذلك.",
          ru: "Доступ к микрофону заблокирован. Можно написать текстом.",
        }));
        stoppedByUserRef.current = true;
      } else if (code === "no-speech") {
        /* Don't surface "I did not catch that" while in continuous
           mode -- it fires too often during natural pauses. Just
           let the auto-restart in onend take care of it. */
      } else if (code === "audio-capture") {
        setVoiceError(t({
          en: "No microphone found. You can type instead.",
          ar: "لم يتم العثور على ميكروفون. يمكنك الكتابة بدلاً من ذلك.",
          ru: "Микрофон не найден. Можно написать текстом.",
        }));
        stoppedByUserRef.current = true;
      } else if (code === "aborted") {
        /* The browser aborted (we called stop() or another instance
           started). Don't restart, don't show a message. */
      } else {
        setVoiceError(t({
          en: "Voice input is unavailable. You can type instead.",
          ar: "الإدخال الصوتي غير متاح. يمكنك الكتابة بدلاً من ذلك.",
          ru: "Голосовой ввод недоступен. Можно написать текстом.",
        }));
        stoppedByUserRef.current = true;
      }
    };

    recog.onend = () => {
      /* If the parent didn't ask to stop, restart so the experience
         feels like one continuous listening session. */
      if (!stoppedByUserRef.current) {
        try {
          recog.start();
          return;
        } catch {
          /* Some browsers throw if start() is called too soon after
             end. Fall through to the stopped state in that case. */
        }
      }
      setListening(false);
      recogRef.current = null;
    };

    try {
      recog.start();
      recogRef.current = recog;
      setListening(true);
      /* Focus the textarea so further typed edits land naturally. */
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch {
      setVoiceError(t({
        en: "Voice input could not start. You can type instead.",
        ar: "لم يبدأ الإدخال الصوتي. يمكنك الكتابة بدلاً من ذلك.",
        ru: "Не удалось запустить голосовой ввод. Можно написать текстом.",
      }));
      setListening(false);
    }
  };

  const chatWithKido = t({ en: "Chat with Kido", ar: "تحدث مع Kido", ru: "Чат с Kido" });

  /* The widget is keyed by sessionId so a future hard reset (e.g. close
     and reopen across days) won't reuse stale state. */
  return (
    <div className="kido" data-session={sessionId}>
      {!open && (
        <button
          type="button"
          className="kido-float"
          onClick={() => setOpen(true)}
          aria-label={chatWithKido}
        >
          <span className="kido-float-ring"  aria-hidden="true" />
          <span className="kido-float-core"  aria-hidden="true">
            <span className="kido-float-bubble">{I_CHAT}</span>
          </span>
          <span className="kido-float-dot" aria-hidden="true" />
          <span className="kido-float-label">{chatWithKido}</span>
        </button>
      )}

      {open && (
        <div className="kido-panel" role="dialog" aria-label={chatWithKido}>
          <header className="kido-header">
            <div className="kido-header-id">
              <span className="kido-avatar" aria-hidden="true">K</span>
              <div>
                <p className="kido-name">Kido</p>
                <p className="kido-status">
                  {t({
                    en: "Friendly Kidsology assistant",
                    ar: "مساعد Kidsology الودود",
                    ru: "Дружелюбный помощник Kidsology",
                  })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="kido-close"
              onClick={() => setOpen(false)}
              aria-label={t({ en: "Close chat", ar: "إغلاق المحادثة", ru: "Закрыть чат" })}
            >
              {I_CLOSE}
            </button>
          </header>

          <div className="kido-messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div key={i} className={`kido-msg kido-msg--${m.role}`}>
                <p>{m.content}</p>
              </div>
            ))}

            {messages.length === 1 && !sending && (
              <div
                className="kido-starters"
                role="group"
                aria-label={t({
                  en: "Starter questions",
                  ar: "أسئلة للبدء",
                  ru: "Вопросы для начала",
                })}
              >
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="kido-starter"
                    onClick={() => send(t(s))}
                  >
                    {t(s)}
                  </button>
                ))}
              </div>
            )}

            {sending && (
              <div className="kido-msg kido-msg--assistant kido-msg--typing" aria-live="polite">
                <span className="kido-dots"><i /><i /><i /></span>
              </div>
            )}
          </div>

          {(listening || voiceError) && (
            <div
              className={`kido-voice-status${listening ? " is-listening" : ""}`}
              role="status"
              aria-live="polite"
            >
              {listening
                ? (
                  <>
                    <span className="kido-voice-wave" aria-hidden="true"><i /><i /><i /></span>
                    {" "}
                    {t({
                      en: "Listening… tap the mic to stop",
                      ar: "أستمع… اضغط الميكروفون للتوقف",
                      ru: "Слушаю… нажмите микрофон, чтобы остановить",
                    })}
                  </>
                )
                : voiceError}
            </div>
          )}

          <form
            className="kido-input"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            {/* Voice input via Web Speech API. The button is rendered
                only when the browser supports SpeechRecognition; on
                unsupported browsers (Firefox without flags, older
                Safari) it stays hidden and the parent uses text. */}
            {voiceSupported && (
              <button
                type="button"
                data-prerender="omit"
                className={`kido-mic${listening ? " is-listening" : ""}`}
                aria-label={listening
                  ? t({
                      en: "Stop voice input",
                      ar: "أوقف الإدخال الصوتي",
                      ru: "Остановить голосовой ввод",
                    })
                  : t({
                      en: "Start voice input",
                      ar: "ابدأ الإدخال الصوتي",
                      ru: "Начать голосовой ввод",
                    })}
                aria-pressed={listening}
                onClick={toggleVoice}
              >
                {I_MIC}
              </button>
            )}
            <textarea
              ref={inputRef}
              className="kido-textarea"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              /* dir="auto" so a parent typing Arabic on the English
                 site still gets right-to-left input, and the reverse. */
              dir="auto"
              placeholder={listening
                ? t({ en: "Listening…", ar: "أستمع…", ru: "Слушаю…" })
                : t({ en: "Ask Kido", ar: "اسأل Kido", ru: "Спросите Kido" })}
              rows={1}
              aria-label={t({
                en: "Message Kido",
                ar: "اكتب رسالة إلى Kido",
                ru: "Сообщение для Kido",
              })}
            />
            <button
              type="submit"
              className="kido-send"
              disabled={sending || !input.trim()}
              aria-label={t({
                en: "Send message",
                ar: "أرسل الرسالة",
                ru: "Отправить сообщение",
              })}
            >
              {I_SEND}
            </button>
          </form>

          <p className="kido-disclaimer">
            {t({
              en: "Kido can make mistakes. For anything important, ",
              ar: "قد يخطئ Kido. لأي أمر مهم، ",
              ru: "Kido может ошибаться. По важным вопросам ",
            })}
            <a href={lp("/#contact")}>
              {t({
                en: "message our team",
                ar: "راسل فريقنا",
                ru: "напишите нашей команде",
              })}
            </a>.
          </p>
        </div>
      )}
    </div>
  );
}
